/**
 * USER SYNC SERVICE
 * Creates users in Firebase Auth and Firestore
 * Ensures POS users are available in Firestore for authentication
 * NO DJANGO - 100% Firebase only
 */

import {
  collection,
  doc,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
  limit
} from '../services/firestoreCompat';
import { getAuth } from 'firebase/auth';
import { db, auth } from './firebaseConfig';

const CREATE_USER_ENDPOINT = '__SAHEEH_FUNCTIONS_URL_UNSET__/createUserWithPassword';

// Verify Firebase instance — guarded because `db` is null in Saheeh
// (Firestore is replaced by the firestoreCompat shim, which returns null
// from getFirestore()). Reaching for `db.app` here used to crash the
// entire admin bundle at module load, leaving the page completely blank.
if ((db as any)?.app) {
  console.log('🔥 [USER SYNC] Using Firestore project:', (db as any).app.options.projectId);
}
if ((auth as any)?.app) {
  console.log('🔥 [USER SYNC] Using Auth domain:', (auth as any).app.options.authDomain);
}

export interface UserSyncData {
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'manager' | 'cashier' | 'staff' | 'rider';
  restaurant_id?: string;
  branch?: string;
  is_active: boolean;
  phone?: string;
  password?: string; // Only used when creating new user
}

class UserSyncService {
  private static instance: UserSyncService;
  
  public static getInstance(): UserSyncService {
    if (!UserSyncService.instance) {
      UserSyncService.instance = new UserSyncService();
    }
    return UserSyncService.instance;
  }

  /**
   * Create user in Firebase Auth and Firestore
   * Creates user document in both 'users' (POS) and 'admin_users' (Admin Panel) collections
   * NO DJANGO - Pure Firebase
   */
  async syncUserToFirestore(
    userData: UserSyncData
  ): Promise<{ success: boolean; firestoreUserId?: string; error?: string }> {
    try {
      console.log('🔄 [USER SYNC] Syncing user to Firestore:', userData.email);
      
      // Step 1: Check if user already exists in Firestore by email
      const existingUser = await this.findUserByEmail(userData.email);
      
      if (existingUser) {
        console.log('⚠️ [USER SYNC] User exists in Firestore, updating...');
        // Update existing user
        await updateDoc(doc(db, 'users', existingUser.id), {
          name: `${userData.first_name} ${userData.last_name}`.trim(),
          email: userData.email,
          role: userData.role,
          restaurant_id: userData.restaurant_id,
          branch: userData.branch,
          is_active: userData.is_active,
          phone: userData.phone,
          updated_at: new Date().toISOString(),
        });
        
        return {
          success: true,
          firestoreUserId: existingUser.id,
        };
      }

      // Step 2: Create Firebase Auth user via backend cloud function (Admin SDK).
      //
      // IMPORTANT: we used to call the client SDK's createUserWithEmailAndPassword here,
      // but that signs the admin out and signs in as the new user — so from the admin's
      // perspective the dialog silently "fails" (they lose their session and the next
      // admin-only API call 403s). Routing through createUserWithPassword keeps the
      // admin session intact and uses Admin SDK on the server.
      let firebaseUid: string;

      if (userData.password) {
        const currentUser = getAuth().currentUser;
        if (!currentUser) {
          throw new Error('Not authenticated. Please log in again.');
        }
        const token = await currentUser.getIdToken(true);

        const resp = await fetch(CREATE_USER_ENDPOINT, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: userData.email,
            password: userData.password,
          }),
        });

        const result = await resp.json().catch(() => ({}));
        if (!resp.ok || !result?.success || !result?.uid) {
          throw new Error(result?.error || `Failed to create auth user (HTTP ${resp.status})`);
        }
        firebaseUid = result.uid;
        console.log('✅ [USER SYNC] Firebase Auth user created via cloud function:', firebaseUid);
      } else {
        // Generate a new UID for Firestore document
        firebaseUid = `user_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      }

      // Step 3: Create/Update Firestore user document in BOTH collections
      // 'users' collection - for POS authentication
      const userDocRef = doc(db, 'users', firebaseUid);
      
      const firestoreUserData = {
        uid: firebaseUid,
        email: userData.email,
        name: `${userData.first_name} ${userData.last_name}`.trim(),
        first_name: userData.first_name,
        last_name: userData.last_name,
        role: userData.role,
        restaurant_id: userData.restaurant_id || null,
        branch: userData.branch || null,
        is_active: userData.is_active,
        phone: userData.phone || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_login: null,
        permissions: this.getDefaultPermissions(userData.role),
      };

      await setDoc(userDocRef, firestoreUserData, { merge: true });
      
      // Also create in 'admin_users' collection - for Admin Panel visibility
      const adminUserDocRef = doc(db, 'admin_users', firebaseUid);
      const adminUserData = {
        id: firebaseUid,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        role: userData.role,
        is_active: userData.is_active,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      await setDoc(adminUserDocRef, adminUserData, { merge: true });
      
      console.log('✅ [USER SYNC] User synced to both "users" and "admin_users" collections');
      
      console.log('✅ [USER SYNC] User synced to Firestore:', firebaseUid);
      
      return {
        success: true,
        firestoreUserId: firebaseUid,
      };
    } catch (error: any) {
      console.error('❌ [USER SYNC] Error syncing user:', error);
      return {
        success: false,
        error: error.message || 'Failed to sync user to Firestore',
      };
    }
  }

  /**
   * Find user in Firestore by email
   */
  private async findUserByEmail(email: string): Promise<{ id: string } | null> {
    try {
      const q = query(
        collection(db, 'users'),
        where('email', '==', email),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        return { id: snapshot.docs[0].id };
      }
      return null;
    } catch (error) {
      console.error('❌ [USER SYNC] Error finding user:', error);
      return null;
    }
  }

  /**
   * Get default permissions based on role
   */
  private getDefaultPermissions(role: string): string[] {
    switch (role) {
      case 'admin':
        return ['read_orders', 'write_orders', 'manage_users', 'manage_settings'];
      case 'manager':
        return ['read_orders', 'write_orders', 'manage_staff'];
      case 'cashier':
      case 'staff':
        return ['read_orders', 'write_orders'];
      default:
        return [];
    }
  }

  /**
   * Update user status in Firestore
   */
  async updateUserStatus(email: string, isActive: boolean): Promise<boolean> {
    try {
      const user = await this.findUserByEmail(email);
      if (user) {
        await updateDoc(doc(db, 'users', user.id), {
          is_active: isActive,
          updated_at: new Date().toISOString(),
        });
        console.log('✅ [USER SYNC] User status updated:', email, isActive);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ [USER SYNC] Error updating user status:', error);
      return false;
    }
  }

  /**
   * Delete user from Firestore (marks as inactive)
   */
  async deleteUserFromFirestore(email: string): Promise<boolean> {
    try {
      const user = await this.findUserByEmail(email);
      if (user) {
        await updateDoc(doc(db, 'users', user.id), {
          is_active: false,
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
        console.log('✅ [USER SYNC] User marked as deleted:', email);
        return true;
      }
      return false;
    } catch (error) {
      console.error('❌ [USER SYNC] Error deleting user:', error);
      return false;
    }
  }
}

export const userSyncService = UserSyncService.getInstance();
export default userSyncService;

